import React from "react";

import { ACCEPT_ALL_AI_SUGGESTIONS_EVENT, salesforcePanelEmitter } from ".";
import { faCheck, faUndoAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";

import { IconButton, Input } from "~/ui/legacyFellowIndex";

import { getInputTypeFromSalesforceType } from "./utils";

import type { SalesforceQuery$data } from "~/__generated__/SalesforceQuery.graphql";

import styles from "./css/FieldRow.module.css";

export type SalesforceObjectRecordType = NonNullable<SalesforceQuery$data["salesforceContactsForStream"]>[number];

type FieldProps = SalesforceObjectRecordType["fields"][number] & {
	handleFieldChange: (fieldName: string, originalValue: string, newValue: string) => void;
	undoTrigger: number;
	aiSuggestion?: string;
};

export default function FieldRow({
	name,
	label,
	value,
	type,
	interactionType,
	handleFieldChange,
	undoTrigger,
	aiSuggestion,
	length,
}: FieldProps) {
	const hasValue = !!value;
	const nonNullValue = value ?? "";
	const [controlledValue, setControlledValue] = React.useState<string>(nonNullValue);
	const [wasEdited, setWasEdited] = React.useState<boolean>(false);
	const [isFocused, setIsFocused] = React.useState<boolean>(false);
	const prevValueRef = React.useRef<string>(nonNullValue);
	const isEditable = interactionType === "EDITABLE" || interactionType === "AI_SUGGESTION";

	const onFieldChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setControlledValue(e.target.value);
	}, []);

	const onUndoField = React.useCallback(() => {
		setControlledValue(nonNullValue);
		setWasEdited(false);
		handleFieldChange(name, nonNullValue, nonNullValue);
	}, [handleFieldChange, name, nonNullValue]);

	const onFieldBlur = React.useCallback(() => {
		if (nonNullValue === controlledValue) {
			setWasEdited(false);
		} else {
			setWasEdited(true);
		}

		setIsFocused(false);
		handleFieldChange(name, nonNullValue, controlledValue);
	}, [handleFieldChange, name, nonNullValue, controlledValue]);

	const onFieldFocus = React.useCallback(() => {
		setIsFocused(true);
	}, []);

	const onFieldKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.currentTarget.blur();
		}
	}, []);

	// Check if value is different from the previous render,
	// meaning the value was updated in Salesforce and we need to reset the field edit state
	React.useEffect(() => {
		if (prevValueRef.current !== nonNullValue) {
			prevValueRef.current = nonNullValue;
			onUndoField();
		}
	}, [nonNullValue, onUndoField]);

	React.useEffect(() => {
		// Reset field value to original value when undoTrigger changes
		onUndoField();
	}, [onUndoField, undoTrigger]);

	const acceptAiSuggestion = React.useCallback(() => {
		if (!aiSuggestion) return;
		setControlledValue(aiSuggestion);
		setWasEdited(true);
		handleFieldChange(name, nonNullValue, aiSuggestion ?? "");
	}, [handleFieldChange, name, nonNullValue, aiSuggestion]);

	React.useEffect(() => {
		salesforcePanelEmitter.on(ACCEPT_ALL_AI_SUGGESTIONS_EVENT, acceptAiSuggestion);
		return () => {
			salesforcePanelEmitter.off(ACCEPT_ALL_AI_SUGGESTIONS_EVENT, acceptAiSuggestion);
		};
	}, [acceptAiSuggestion]);

	const renderFieldValue = React.useCallback(() => {
		const inputType = getInputTypeFromSalesforceType(type);

		if (isEditable && inputType) {
			return (
				<Input
					className={cx(styles.input, styles.expandedWidth)}
					value={controlledValue}
					type={inputType}
					placeholder="Type here..."
					onChange={onFieldChange}
					onBlur={onFieldBlur}
					onFocus={onFieldFocus}
					onKeyDown={onFieldKeyDown}
					maxLength={length ?? undefined}
					name={`salesforce_sidebar_field_${name}`}
				/>
			);
		}

		return <p className={cx(styles.content, styles.truncate, { [styles.noData]: !hasValue })}>{value ?? "No data"}</p>;
	}, [
		controlledValue,
		hasValue,
		isEditable,
		length,
		onFieldBlur,
		onFieldChange,
		onFieldFocus,
		onFieldKeyDown,
		type,
		value,
		name,
	]);

	return (
		<div className={styles.field}>
			<div className={styles.labelHeader}>
				<label className={cx(styles.label, styles.truncate)}>{label}</label>
				{Boolean(length) && isFocused && (
					<span className={styles.characterCount}>
						{controlledValue.length}/{length}
					</span>
				)}
				{wasEdited && <IconButton title="Undo" icon={faUndoAlt} onClick={onUndoField} className={styles.undoButton} />}
			</div>

			{interactionType === "AI_SUGGESTION" && aiSuggestion && !wasEdited ? (
				<div className={styles.aiSuggestionContainer}>
					<div className={cx(styles.aiSuggestionInput)}>
						{value && <div className={styles.originalValue}>{value}</div>}
						<div className={styles.aiSuggestionValue}>{aiSuggestion}</div>
					</div>
					<IconButton
						className={styles.acceptAiSuggestionButton}
						title="Accept AI suggestion"
						icon={faCheck}
						onClick={acceptAiSuggestion}
						variant="primary-ai"
					/>
				</div>
			) : (
				renderFieldValue()
			)}
		</div>
	);
}
